import React, { useState } from "react";
import styled from "styled-components";
import { lightThemeColors } from "../../assets/styles/colors";

const RealtimeConnectorDocumentation = () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL || "";
  const dictationEndpoint = `${API_BASE_URL.replace(/\/?$/, "/")}api/v1/connector/realtime/dictation`;
  const conversationEndpoint = `${API_BASE_URL.replace(/\/?$/, "/")}api/v1/connector/realtime/conversation`;

  const [activeTab, setActiveTab] = useState("dictation");

  const dictationCurl = `curl --location --globoff '${dictationEndpoint}' \\
     --header 'Content-Type: application/json' \\
     --header 'X-API-KEY: <YOUR_API_KEY>' \\
     --data '{
       "configuration": {
         "translations_enabled": true,
         "stt_enabled": false,
         "tts_enabled": true,
         "tts_speed": 1.99,
         "tts_voice_gender": "female",
         "language": "en-US",
         "from": {
           "language": "en-US",
           "born_in": "1990-01-01",
           "gender": "female",
           "cultural_background": "American",
           "age": "34"
         },
         "to": {
           "language": "ro-RO",
           "born_in": "2012-01-01",
           "gender": "female",
           "cultural_background": "Romanian",
           "age": "12"
         },
         "mood": "happy"
       }
     }'`;

  const conversationCurl = `curl --location '${conversationEndpoint}' \\
     --header 'Content-Type: application/json' \\
     --header 'X-API-KEY: <YOUR_API_KEY>' \\
     --data '{
       "configuration": {
         "translations_enabled": true,
         "stt_enabled": false,
         "tts_enabled": true,
         "language_b": "en-EN",
         "language_a": "",
         "tts_speed": 1.25,
         "tts_voice_gender": "neutral"
       }
     }'`;

  const updateDictation = `{
  "type": "update_configuration",
  "configuration": {
    "translations_enabled": true,
    "stt_enabled": true,
    "tts_enabled": true,
    "tts_speed": 1.5,
    "language": "en-US",
    "from": {
      "language": "en-US",
      "born_in": "1990-01-01",
      "gender": "female",
      "cultural_background": "American",
      "age": "34"
    },
    "to": {
      "language": "ro-RO",
      "born_in": "2012-01-01",
      "gender": "female",
      "cultural_background": "Romanian",
      "age": "12"
    },
    "mood": "happy"
  }
}`;

  const updateConversation = `{
  "type": "update_configuration",
  "configuration": {
    "translations_enabled": true,
    "stt_enabled": true,
    "tts_enabled": true,
    "language_a": "en-EN",
    "language_b": "ro-RO",
    "tts_speed": 1.25,
    "tts_voice_gender": "neutral"
  }
}`;

  const translateText = `{
  "type": "translate_text",
  "text": "Ce faci?"
}`;

  const errorResponse = `{
  "type": "error",
  "code": "invalid_message",
  "message": "invalid message type"
}`;

  const configurationUpdated = `{
  "type": "configuration_updated",
  "configuration": {
    "translations_enabled": true,
    "stt_enabled": true,
    "tts_enabled": true,
    "language": "en-US",
    "from": {},
    "to": {},
    "mood": "happy"
  }
}`;

  const textTranslated = `{
  "type": "text_translated",
  "text": "What are you up to?",
  "from_languages": ["ro"],
  "to_language": "en-US"
}`;

  const textRecognized = `{
  "type": "text_recognized",
  "text": "Tacos al pastor are my favorite.",
  "language": "en"
}`;

  const quotaAudioMinutes = `{
    "type": "quota_exceeded",
    "code": "audio_minutes_exhausted",
    "message": "Audio minutes quota exceeded. STT and TTS have been disabled.",
    "disabled_features": ["stt", "tts"]
  }`;

  const quotaTranslationTokens = `{
    "type": "quota_exceeded",
    "code": "translation_tokens_exhausted",
    "message": "Translation tokens quota exceeded. TTS and Translation have been disabled.",
    "disabled_features": ["tts", "translation"]
  }`;

  return (
    <Container>
      <HeaderRow>
        <Title data-emotii-exclude>
          Realtime Connector – WebSocket Protocol Specification
        </Title>
        <HeaderMeta>
          <Badge tone="brand" data-emotii-exclude>v1</Badge>
          <DividerDot />
          <Small>Realtime • WebSocket</Small>
        </HeaderMeta>
      </HeaderRow>

      <PageContent>
        <Grid>
          <Card $span2>
            <CardHeader>
              <CardTitle>1. Overview</CardTitle>
            </CardHeader>
            <Note>The Realtime Connector uses:</Note>
            <Table>
              <thead>
                <tr>
                  <TableHeader>Component</TableHeader>
                  <TableHeader>Purpose</TableHeader>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <TableCell>REST</TableCell>
                  <TableCell>Session creation &amp; authorization</TableCell>
                </tr>
                <tr>
                  <TableCell>WebSocket</TableCell>
                  <TableCell>Realtime bidirectional communication</TableCell>
                </tr>
                <tr>
                  <TableCell>JSON frames</TableCell>
                  <TableCell>Control, configuration, and text messages</TableCell>
                </tr>
                <tr>
                  <TableCell>Binary frames</TableCell>
                  <TableCell>Raw audio streaming (STT input &amp; TTS output)</TableCell>
                </tr>
              </tbody>
            </Table>
            <Note>
              Once a WebSocket connection is established,{" "}
              <b>all runtime behavior is controlled exclusively through WebSocket messages.</b>
            </Note>
          </Card>

          <Card $span2>
            <CardHeader>
              <CardTitle>2. Authentication</CardTitle>
            </CardHeader>
            <Note>All sessions are authenticated using an API key.</Note>
            <SubTitle>2.1 API Key</SubTitle>
            <Table>
              <thead>
                <tr>
                  <TableHeader>Header</TableHeader>
                  <TableHeader>Required</TableHeader>
                  <TableHeader>Description</TableHeader>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <TableCell>X-API-KEY</TableCell>
                  <TableCell>Yes</TableCell>
                  <TableCell>API key that authorizes the session and binds the WebSocket</TableCell>
                </tr>
              </tbody>
            </Table>
            <Note>The same key is used for:</Note>
            <List>
              <Item>
                <Key>REST</Key>
                <Desc></Desc>
              </Item>
            </List>
          </Card>

          <Card $span2>
            <CardHeader>
              <CardTitle>2.2 WebSocket URLs</CardTitle>
            </CardHeader>
            <SubTitle>Dictation Mode</SubTitle>
            <EndpointRow>
              <EndpointLabel>POST</EndpointLabel>
              <EndpointValue data-emotii-exclude>
                {dictationEndpoint}
              </EndpointValue>
            </EndpointRow>
            <Note>
              <b>Example curl</b>
            </Note>
            <CodeBlock>
              <CodeArea data-emotii-exclude>
                <code>{dictationCurl}</code>
              </CodeArea>
            </CodeBlock>
            <Note>
              <b>Notes</b>
            </Note>
            <List>
              <Item>
                <Key>from</Key>
                <Desc><b>optional</b> in Dictation Mode.</Desc>
              </Item>
              <Item>
                <Key>to</Key>
                <Desc><b>optional</b> in Dictation Mode.</Desc>
              </Item>
              <Item>
                <Key>mood</Key>
                <Desc><b>optional</b> in Dictation Mode.</Desc>
              </Item>
              <Item>
                <Key>tts_speed</Key>
                <Desc><b>optional</b> - TTS playback speed (1.0 - 2.0). Defaults to 1.0 if omitted.</Desc>
              </Item>
              <Item>
                <Key>tts_voice_gender</Key>
                <Desc><b>optional</b> - TTS voice gender: <code>male</code>, <code>female</code>, or <code>neutral</code>. Defaults to <code>neutral</code> if omitted.</Desc>
              </Item>
            </List>
            <Note style={{ marginTop: 2 }}>
              The response includes a WebSocket URL to connect to, plus the validated configuration you should treat as source of truth.
            </Note>
            <SubTitle>Conversation Mode</SubTitle>
            <EndpointRow>
              <EndpointLabel>POST</EndpointLabel>
              <EndpointValue data-emotii-exclude>
                {conversationEndpoint}
              </EndpointValue>
            </EndpointRow>
            <Note>
              <b>Example curl</b>
            </Note>
            <CodeBlock>
              <CodeArea data-emotii-exclude>
                <code>{conversationCurl}</code>
              </CodeArea>
            </CodeBlock>
            <Note>
              <b>Configuration rules (Conversation Mode)</b>
            </Note>
            <List>
              <Item>
                <Key>translations_enabled</Key>
                <Desc>
                  <b>Mandatory</b>
                </Desc>
              </Item>
              <Item>
                <Key>stt_enabled</Key>
                <Desc>
                  <b>Mandatory</b>
                </Desc>
              </Item>
              <Item>
                <Key>tts_enabled</Key>
                <Desc>
                  <b>Mandatory</b>
                </Desc>
              </Item>
              <Item>
                <Key>language_b</Key>
                <Desc>
                  <b>Mandatory</b>
                </Desc>
              </Item>
              <Item>
                <Key>language_a</Key>
                <Desc>
                  <b>Optional</b>
                </Desc>
              </Item>
              <Item>
                <Key>tts_speed</Key>
                <Desc>
                  <b>Optional</b> - TTS playback speed (1.0 - 2.0). Defaults to 1.0 if omitted.
                </Desc>
              </Item>
              <Item>
                <Key>tts_voice_gender</Key>
                <Desc>
                  <b>Optional</b> - TTS voice gender: <code>male</code>, <code>female</code>, or <code>neutral</code>. Defaults to <code>neutral</code> if omitted.
                </Desc>
              </Item>
            </List>
          </Card>

          <Card $span2>
            <CardHeader>
              <CardTitle>3. Transport Rules</CardTitle>
            </CardHeader>
            <SubTitle>3.1 WebSocket Frames</SubTitle>
            <Table>
              <thead>
                <tr>
                  <TableHeader>Frame Type</TableHeader>
                  <TableHeader>Usage</TableHeader>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <TableCell>Text (UTF-8 JSON)</TableCell>
                  <TableCell>Configuration, control, text, translation</TableCell>
                </tr>
                <tr>
                  <TableCell>Binary</TableCell>
                  <TableCell>Audio data only</TableCell>
                </tr>
              </tbody>
            </Table>
            <Note>
              ❗ <b>Audio is never wrapped in JSON.</b>
            </Note>
            <Note>Frame type must be used to distinguish audio from control messages.</Note>
            <List>
              <Item>
                <Key>Audio input encoding</Key>
                <Desc>16000 PCM</Desc>
              </Item>
              <Item>
                <Key>Audio output encoding</Key>
                <Desc>24000 PCM</Desc>
              </Item>
            </List>
          </Card>

          <Card $span2>
            <CardHeader>
              <CardTitle>4. Configuration Object (Canonical Schema)</CardTitle>
            </CardHeader>
            <Note>
              The <b>configuration object</b> defines the runtime behavior of the session. It is:
            </Note>
            <BulletList>
              <BulletItem>Sent by the client (update_configuration)</BulletItem>
              <BulletItem>Echoed by the server (configuration_updated)</BulletItem>
              <BulletItem>The single source of truth for runtime state</BulletItem>
            </BulletList>
            <SubTitle>4.1 Configuration Fields</SubTitle>
            <Table>
              <thead>
                <tr>
                  <TableHeader>Field</TableHeader>
                  <TableHeader>Type</TableHeader>
                  <TableHeader>Required</TableHeader>
                  <TableHeader>Mode</TableHeader>
                  <TableHeader>Description</TableHeader>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <TableCell>translations_enabled</TableCell>
                  <TableCell>boolean</TableCell>
                  <TableCell>Yes</TableCell>
                  <TableCell>All</TableCell>
                  <TableCell>Enables translation pipeline</TableCell>
                </tr>
                <tr>
                  <TableCell>stt_enabled</TableCell>
                  <TableCell>boolean</TableCell>
                  <TableCell>Yes</TableCell>
                  <TableCell>All</TableCell>
                  <TableCell>Enables speech-to-text</TableCell>
                </tr>
                <tr>
                  <TableCell>tts_enabled</TableCell>
                  <TableCell>boolean</TableCell>
                  <TableCell>Yes</TableCell>
                  <TableCell>All</TableCell>
                  <TableCell>Enables text-to-speech</TableCell>
                </tr>
                <tr>
                  <TableCell>language</TableCell>
                  <TableCell>string</TableCell>
                  <TableCell>Yes</TableCell>
                  <TableCell>Dictation</TableCell>
                  <TableCell>Primary language (BCP-47, e.g. en-US)</TableCell>
                </tr>
                <tr>
                  <TableCell>language_a</TableCell>
                  <TableCell>string</TableCell>
                  <TableCell>No</TableCell>
                  <TableCell>Conversation</TableCell>
                  <TableCell>Optional secondary language</TableCell>
                </tr>
                <tr>
                  <TableCell>language_b</TableCell>
                  <TableCell>string</TableCell>
                  <TableCell>Yes</TableCell>
                  <TableCell>Conversation</TableCell>
                  <TableCell>Primary conversation language</TableCell>
                </tr>
                <tr>
                  <TableCell>from</TableCell>
                  <TableCell>object</TableCell>
                  <TableCell>No</TableCell>
                  <TableCell>Dictation</TableCell>
                  <TableCell>Persona metadata (source)</TableCell>
                </tr>
                <tr>
                  <TableCell>to</TableCell>
                  <TableCell>object</TableCell>
                  <TableCell>No</TableCell>
                  <TableCell>Dictation</TableCell>
                  <TableCell>Persona metadata (target)</TableCell>
                </tr>
                <tr>
                  <TableCell>mood</TableCell>
                  <TableCell>string</TableCell>
                  <TableCell>No</TableCell>
                  <TableCell>Dictation</TableCell>
                  <TableCell>Emotional / tonal hint</TableCell>
                </tr>
                <tr>
                  <TableCell>tts_speed</TableCell>
                  <TableCell>number</TableCell>
                  <TableCell>No</TableCell>
                  <TableCell>All</TableCell>
                  <TableCell>TTS playback speed (1.0 - 2.0). Defaults to 1.0</TableCell>
                </tr>
                <tr>
                  <TableCell>tts_voice_gender</TableCell>
                  <TableCell>string</TableCell>
                  <TableCell>No</TableCell>
                  <TableCell>All</TableCell>
                  <TableCell>TTS voice gender: male, female, or neutral. Defaults to neutral</TableCell>
                </tr>
              </tbody>
            </Table>
          </Card>

          <Card $span2>
            <CardHeader>
              <CardTitle>4.2 Persona Object (from, to)</CardTitle>
            </CardHeader>
            <Note>Persona objects influence translation style and TTS voice selection.</Note>
            <Table>
              <thead>
                <tr>
                  <TableHeader>Field</TableHeader>
                  <TableHeader>Type</TableHeader>
                  <TableHeader>Required</TableHeader>
                  <TableHeader>Description</TableHeader>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <TableCell>language</TableCell>
                  <TableCell>string</TableCell>
                  <TableCell>No</TableCell>
                  <TableCell>Persona language</TableCell>
                </tr>
                <tr>
                  <TableCell>born_in</TableCell>
                  <TableCell>string (ISO date)</TableCell>
                  <TableCell>No</TableCell>
                  <TableCell>Birth date</TableCell>
                </tr>
                <tr>
                  <TableCell>gender</TableCell>
                  <TableCell>string</TableCell>
                  <TableCell>No</TableCell>
                  <TableCell>Gender identity</TableCell>
                </tr>
                <tr>
                  <TableCell>cultural_background</TableCell>
                  <TableCell>string</TableCell>
                  <TableCell>No</TableCell>
                  <TableCell>Cultural context</TableCell>
                </tr>
                <tr>
                  <TableCell>age</TableCell>
                  <TableCell>string</TableCell>
                  <TableCell>No</TableCell>
                  <TableCell>Age (string for flexibility)</TableCell>
                </tr>
              </tbody>
            </Table>
          </Card>

          <Card $span2>
            <CardHeader>
              <CardTitle>5. WebSocket Messages — Client → Proxy</CardTitle>
            </CardHeader>
            <SubTitle>5.1 Message Summary</SubTitle>
            <Table>
              <thead>
                <tr>
                  <TableHeader>Message</TableHeader>
                  <TableHeader>Frame</TableHeader>
                  <TableHeader>Purpose</TableHeader>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <TableCell>update_configuration</TableCell>
                  <TableCell>JSON</TableCell>
                  <TableCell>Update runtime configuration</TableCell>
                </tr>
                <tr>
                  <TableCell>translate_text</TableCell>
                  <TableCell>JSON</TableCell>
                  <TableCell>Translate a text string</TableCell>
                </tr>
                <tr>
                  <TableCell>Audio stream</TableCell>
                  <TableCell>Binary</TableCell>
                  <TableCell>Send audio for STT</TableCell>
                </tr>
              </tbody>
            </Table>
            <SubTitle>5.2 update_configuration — Dictation Mode</SubTitle>
            <Note>Used to set or update runtime behavior for dictation.</Note>
            <CodeBlock>
              <CodeArea data-emotii-exclude>
                <code>{updateDictation}</code>
              </CodeArea>
            </CodeBlock>
            <Note>
              <b>Rules</b>
            </Note>
            <BulletList>
              <BulletItem>Missing optional fields are ignored</BulletItem>
              <BulletItem>Any update replaces the previous runtime configuration</BulletItem>
              <BulletItem>Server always responds with configuration_updated</BulletItem>
            </BulletList>
            <SubTitle>5.3 update_configuration — Conversation Mode</SubTitle>
            <Note>Same message type, different configuration shape.</Note>
            <CodeBlock>
              <CodeArea data-emotii-exclude>
                <code>{updateConversation}</code>
              </CodeArea>
            </CodeBlock>
            <Note>
              <b>Rules</b>
            </Note>
            <BulletList>
              <BulletItem>language_b is mandatory</BulletItem>
              <BulletItem>language_a is optional</BulletItem>
              <BulletItem>Persona fields are not used in Conversation Mode</BulletItem>
            </BulletList>
            <SubTitle>5.4 translate_text</SubTitle>
            <Note>Requests translation of a text string using the current configuration.</Note>
            <CodeBlock>
              <CodeArea data-emotii-exclude>
                <code>{translateText}</code>
              </CodeArea>
            </CodeBlock>
            <SubTitle>5.5 Audio Input (STT)</SubTitle>
            <Table>
              <thead>
                <tr>
                  <TableHeader>Property</TableHeader>
                  <TableHeader>Value</TableHeader>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <TableCell>Direction</TableCell>
                  <TableCell>Client → Proxy</TableCell>
                </tr>
                <tr>
                  <TableCell>Frame type</TableCell>
                  <TableCell>Binary</TableCell>
                </tr>
                <tr>
                  <TableCell>JSON wrapper</TableCell>
                  <TableCell>❌ Not allowed</TableCell>
                </tr>
                <tr>
                  <TableCell>Purpose</TableCell>
                  <TableCell>Speech-to-Text input</TableCell>
                </tr>
              </tbody>
            </Table>
            <Note>Audio frames may be streamed continuously.</Note>
          </Card>

          <Card $span2>
            <CardHeader>
              <CardTitle>6. WebSocket Messages — Proxy → Client</CardTitle>
            </CardHeader>
            <SubTitle>6.1 Message Summary</SubTitle>
            <Table>
              <thead>
                <tr>
                  <TableHeader>Message</TableHeader>
                  <TableHeader>Frame</TableHeader>
                  <TableHeader>Purpose</TableHeader>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <TableCell>error</TableCell>
                  <TableCell>JSON</TableCell>
                  <TableCell>Error response</TableCell>
                </tr>
                <tr>
                  <TableCell>configuration_updated</TableCell>
                  <TableCell>JSON</TableCell>
                  <TableCell>Configuration acknowledgment</TableCell>
                </tr>
                <tr>
                  <TableCell>text_translated</TableCell>
                  <TableCell>JSON</TableCell>
                  <TableCell>Translation result</TableCell>
                </tr>
                <tr>
                  <TableCell>text_recognized</TableCell>
                  <TableCell>JSON</TableCell>
                  <TableCell>STT result</TableCell>
                </tr>
                <tr>
                  <TableCell>quota_exceeded</TableCell>
                  <TableCell>JSON</TableCell>
                  <TableCell>Quota limit notification</TableCell>
                </tr>
                <tr>
                  <TableCell>Audio stream</TableCell>
                  <TableCell>Binary</TableCell>
                  <TableCell>TTS output</TableCell>
                </tr>
              </tbody>
            </Table>
            <SubTitle>6.2 error</SubTitle>
            <Note>Returned when a message is invalid or unsupported.</Note>
            <CodeBlock>
              <CodeArea data-emotii-exclude>
                <code>{errorResponse}</code>
              </CodeArea>
            </CodeBlock>
            <Table>
              <thead>
                <tr>
                  <TableHeader>Field</TableHeader>
                  <TableHeader>Description</TableHeader>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <TableCell>code</TableCell>
                  <TableCell>Machine-readable error identifier</TableCell>
                </tr>
                <tr>
                  <TableCell>message</TableCell>
                  <TableCell>Human-readable explanation</TableCell>
                </tr>
              </tbody>
            </Table>
            <SubTitle>6.3 configuration_updated</SubTitle>
            <Note>
              Sent after a successful configuration update. This represents the{" "}
              <b>effective runtime state</b>.
            </Note>
            <CodeBlock>
              <CodeArea data-emotii-exclude>
                <code>{configurationUpdated}</code>
              </CodeArea>
            </CodeBlock>
            <SubTitle>6.4 text_translated</SubTitle>
            <Note>Returned when a translation completes.</Note>
            <CodeBlock>
              <CodeArea data-emotii-exclude>
                <code>{textTranslated}</code>
              </CodeArea>
            </CodeBlock>
            <SubTitle>6.5 text_recognized</SubTitle>
            <Note>Returned when speech is recognized from audio input.</Note>
            <CodeBlock>
              <CodeArea data-emotii-exclude>
                <code>{textRecognized}</code>
              </CodeArea>
            </CodeBlock>
            <SubTitle>6.6 Audio Output (TTS)</SubTitle>
            <Table>
              <thead>
                <tr>
                  <TableHeader>Property</TableHeader>
                  <TableHeader>Value</TableHeader>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <TableCell>Direction</TableCell>
                  <TableCell>Proxy → Client</TableCell>
                </tr>
                <tr>
                  <TableCell>Frame type</TableCell>
                  <TableCell>Binary</TableCell>
                </tr>
                <tr>
                  <TableCell>Purpose</TableCell>
                  <TableCell>Text-to-Speech output</TableCell>
                </tr>
                <tr>
                  <TableCell>JSON wrapper</TableCell>
                  <TableCell>❌ Never</TableCell>
                </tr>
              </tbody>
            </Table>
            <Note>Audio frames are playable audio data.</Note>
            
            <SubTitle>6.7 quota_exceeded</SubTitle>
            <Note>
              ⚠️ Sent when a quota dimension is exhausted. Also re-sent if the client attempts to 
              re-enable a blocked feature via <code>update_configuration</code> or <code>translate_text</code>.
            </Note>
            <Note>
              <b>Example 1: Audio Minutes Exhausted</b>
            </Note>
            <CodeBlock>
              <CodeArea data-emotii-exclude>
                <code>{quotaAudioMinutes}</code>
              </CodeArea>
            </CodeBlock>
            <Note>
              <b>Example 2: Translation Tokens Exhausted</b>
            </Note>
            <CodeBlock>
              <CodeArea data-emotii-exclude>
                <code>{quotaTranslationTokens}</code>
              </CodeArea>
            </CodeBlock>
            <Note>
              <b>Field Descriptions</b>
            </Note>
            <Table>
              <thead>
                <tr>
                  <TableHeader>Field</TableHeader>
                  <TableHeader>Description</TableHeader>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <TableCell>code</TableCell>
                  <TableCell>
                    Identifies which quota was exceeded: <code>audio_minutes_exhausted</code> or{" "}
                    <code>translation_tokens_exhausted</code>
                  </TableCell>
                </tr>
                <tr>
                  <TableCell>message</TableCell>
                  <TableCell>Human-readable explanation of the quota violation</TableCell>
                </tr>
                <tr>
                  <TableCell>disabled_features</TableCell>
                  <TableCell>
                    Array of features that have been automatically disabled due to quota exhaustion
                  </TableCell>
                </tr>
              </tbody>
            </Table>
            <Note>
              <b>Quota Impact on Features</b>
            </Note>
            <Table>
              <thead>
                <tr>
                  <TableHeader>Quota Type</TableHeader>
                  <TableHeader>Disabled Features</TableHeader>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <TableCell>Audio Minutes</TableCell>
                  <TableCell>STT, TTS</TableCell>
                </tr>
                <tr>
                  <TableCell>Translation Tokens</TableCell>
                  <TableCell>TTS, Translation</TableCell>
                </tr>
              </tbody>
            </Table>
          </Card>

          <Card $span2>
            <CardHeader>
              <CardTitle>7. Mode Comparison</CardTitle>
            </CardHeader>
            <Table>
              <thead>
                <tr>
                  <TableHeader>Feature</TableHeader>
                  <TableHeader>Dictation</TableHeader>
                  <TableHeader>Conversation</TableHeader>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <TableCell>Languages</TableCell>
                  <TableCell>language</TableCell>
                  <TableCell>language_b (+ optional language_a)</TableCell>
                </tr>
                <tr>
                  <TableCell>Personas</TableCell>
                  <TableCell>Yes</TableCell>
                  <TableCell>No</TableCell>
                </tr>
                <tr>
                  <TableCell>Translation</TableCell>
                  <TableCell>Yes</TableCell>
                  <TableCell>Yes</TableCell>
                </tr>
                <tr>
                  <TableCell>STT</TableCell>
                  <TableCell>Yes</TableCell>
                  <TableCell>Yes</TableCell>
                </tr>
                <tr>
                  <TableCell>TTS</TableCell>
                  <TableCell>Yes</TableCell>
                  <TableCell>Yes</TableCell>
                </tr>
                <tr>
                  <TableCell>Message types</TableCell>
                  <TableCell>Identical</TableCell>
                  <TableCell>Identical</TableCell>
                </tr>
              </tbody>
            </Table>
          </Card>

          <Card $span2>
            <CardHeader>
              <CardTitle>8. Implementation Notes</CardTitle>
            </CardHeader>
            <BulletList>
              <BulletItem>Configuration is stateful</BulletItem>
              <BulletItem>Audio and JSON must be distinguished by frame type</BulletItem>
              <BulletItem>Clients should treat configuration_updated as authoritative</BulletItem>
              <BulletItem>
                Server may emit text_recognized and text_translated independently
              </BulletItem>
              <BulletItem>
                When quota limits are exceeded, the server automatically disables affected features 
                and sends quota_exceeded messages
              </BulletItem>
              <BulletItem>
                Clients attempting to re-enable quota-blocked features will receive additional 
                quota_exceeded messages
              </BulletItem>
            </BulletList>
          </Card>
        </Grid>
      </PageContent>
    </Container>
  );
};

export default RealtimeConnectorDocumentation;

/* ---------------- Styles ---------------- */

const Container = styled.div`
  display: grid;
  grid-template-rows: auto 1fr;
  height: 100%;
  width: 100%;
  background: #fafafa;
  overflow: hidden;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: space-between;
  padding: 16px 24px;
  background: #fff;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  position: sticky;
  top: 0;
  z-index: 1;
`;

const Title = styled.h2`
  color: ${lightThemeColors.darkColor};
  font-size: 18px;
  font-weight: 800;
  line-height: 1.2;
  margin: 0;
`;

const HeaderMeta = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
`;

const Small = styled.span`
  font-size: 12px;
  color: rgba(56, 56, 58, 0.7);
`;

const DividerDot = styled.span`
  width: 4px;
  height: 4px;
  background: rgba(56, 56, 58, 0.35);
  border-radius: 50%;
  display: inline-block;
`;

const Badge = styled.span`
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  background: ${(props) =>
    props.tone === "brand" ? lightThemeColors.primaryColor : "rgba(56,56,58,0.1)"};
  color: ${(props) => (props.tone === "brand" ? "#fff" : lightThemeColors.darkColor)};
`;

const PageContent = styled.div`
  overflow: auto;
  padding: 24px;
`;

const Grid = styled.div`
  display: grid;
  gap: 20px;
  grid-template-columns: repeat(12, 1fr);
  align-items: start;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.section`
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);
  border-radius: 14px;
  padding: 18px;
  grid-column: ${(props) => (props.$span2 ? "span 12" : "span 6")};

  @media (max-width: 1024px) {
    grid-column: 1 / -1;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const CardTitle = styled.h3`
  color: ${lightThemeColors.darkColor};
  font-size: 16px;
  font-weight: 800;
  margin: 0;
`;

const SubTitle = styled.h4`
  color: ${lightThemeColors.darkColor};
  font-size: 14px;
  font-weight: 800;
  margin: 16px 0 8px;
`;

const Note = styled.p`
  color: rgba(56, 56, 58, 0.75);
  font-size: 13px;
  line-height: 1.6;
  margin: 4px 0 14px;
`;

const BulletList = styled.ul`
  list-style: disc;
  list-style-position: outside;
  margin: 0 0 14px;
  padding-left: 20px;
  color: rgba(56, 56, 58, 0.9);
  font-size: 13px;
  line-height: 1.6;
`;

const BulletItem = styled.li`
  margin: 6px 0;
`;

const List = styled.div`
  display: grid;
  gap: 10px;
`;

const Item = styled.div`
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 12px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const Key = styled.div`
  color: ${lightThemeColors.darkColor};
  font-weight: 700;
  font-size: 13px;
  background: #f5f5f5;
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  width: fit-content;
`;

const Desc = styled.div`
  color: rgba(56, 56, 58, 0.9);
  font-size: 13px;
  line-height: 1.6;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 14px;
`;

const TableHeader = styled.th`
  text-align: left;
  font-size: 12px;
  font-weight: 700;
  color: rgba(56, 56, 58, 0.7);
  padding: 6px 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
`;

const TableCell = styled.td`
  font-size: 13px;
  color: ${lightThemeColors.darkColor};
  padding: 6px 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
  vertical-align: top;
`;

const CodeBlock = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(34, 39, 46, 1);
  color: #fff;
  border-radius: 12px;
  overflow: hidden;
`;

const CodeArea = styled.pre`
  font-family: "Menlo", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
  font-size: 12.5px;
  line-height: 1.45;
  padding: 14px;
  margin: 0;
  max-width: 100%;
  overflow: auto;
  white-space: pre;
  word-break: normal;

  code {
    font-family: inherit;
  }
`;

const EndpointRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
`;

const EndpointLabel = styled.div`
  color: #059669;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  flex-shrink: 0;
  background: #d1fae5;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #a7f3d0;
`;

const EndpointValue = styled.div`
  color: ${lightThemeColors.darkColor};
  font-size: 14px;
  font-weight: 600;
  word-break: break-all;
  font-family: "Menlo", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
`;
